import { Box, Heading, Divider, Tooltip, Text, Icon, useDisclosure } from '@chakra-ui/react';
import { EventContentArg } from '@fullcalendar/react';
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { IconThumbsDown, IconThumbsUp } from 'assets/icons';
import { formatYearMonthDayTime } from 'utils/format';
interface IEventContentProps {
  arg: EventContentArg;
}

const EventContent: React.FC<IEventContentProps> = ({ arg }) => {
  const { t } = useTranslation(['common', 'scheduling']);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const enterTimeout = React.useRef<number>();

  const openTooltip = () => {
    enterTimeout.current = window.setTimeout(onOpen, 700);
  };

  const closeTooltip = () => {
    if (enterTimeout.current) {
      clearTimeout(enterTimeout.current);
    }
    onClose();
  };

  return (
    <Tooltip
      isOpen={isOpen}
      className="hidden-print"
      openDelay={700}
      label={
        <Box
          bg="primary.900"
          display="inline-block"
          color="white"
          p="2"
          fontSize="xs"
          borderRadius="lg"
          overflow="auto"
          maxHeight="500px"
        >
          <Heading as="h5" fontWeight="semibold" size="md" mb="1">
            {arg.event.title}
          </Heading>
          {arg.event.extendedProps ? (
            <Text mb="2">
              {arg.event.extendedProps.startDate ? formatYearMonthDayTime(arg.event._def.extendedProps.startDate) : ''}{' '}
              -{' '}
              {arg.event._def.extendedProps.endDate ? formatYearMonthDayTime(arg.event._def.extendedProps.endDate) : ''}
            </Text>
          ) : null}

          {arg.event.extendedProps?.description ? <Text mb="1">{arg.event.extendedProps?.description}</Text> : null}
          <Divider mt="1" mb="2" />
          {arg.event.extendedProps?.job_name ? (
            <Text mb="1">
              <strong>{t('common:job')}:</strong> {arg.event.extendedProps?.job_name}
            </Text>
          ) : null}
          {arg.event.extendedProps?.timezone ? (
            <Text mb="1">
              {' '}
              <strong>{t('common:timezone')}: </strong> {arg.event.extendedProps?.timezone}
            </Text>
          ) : null}
          {arg.event.extendedProps?.employee ? (
            <Text mb="1">
              <strong>{t('common:assignedTo')}:</strong> {arg.event.extendedProps.employee}
            </Text>
          ) : null}

          {arg.event.extendedProps?.notaccepted_employees ? (
            <Text mb="1">
              <strong>{t('common:rejectedBy')}:</strong> {arg.event.extendedProps.notaccepted_employees}
            </Text>
          ) : null}

          {arg.event.extendedProps?.accepted_employees ? (
            <Text mb="1">
              <strong>{t('common:acceptedBy')}:</strong> {arg.event.extendedProps.accepted_employees}
            </Text>
          ) : null}
          {arg.event.extendedProps?.assign_by ? (
            <Text>
              <strong>{t('common:scheduledBy')}:</strong> {arg.event.extendedProps.assign_by}
            </Text>
          ) : null}
        </Box>
      }
      display="block"
      hasArrow
      bg="primary.900"
    >
      <Box width="100%" px="1" onMouseOver={openTooltip} onMouseOut={closeTooltip}>
        <Text isTruncated>
          {arg.event.extendedProps?.is_accepted === true && (
            <Icon as={IconThumbsUp} mr="1" boxSize="10px" sx={{ '& path': { fill: 'white' } }} />
          )}
          {arg.event.extendedProps?.is_accepted === false && (
            <Icon as={IconThumbsDown} mr="1" boxSize="10px" sx={{ '& path': { fill: 'white' } }} />
          )}
          {arg.event.extendedProps?.is_published !== 1 ? t('common:[Draft]') : `${arg.timeText}`} {arg.event.title}
        </Text>
        <Text>{arg.event.extendedProps?.job_name ? arg.event._def.extendedProps.job_name : ''}</Text>
      </Box>
    </Tooltip>
  );
};

export default memo(EventContent);
